import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  LinearProgress,
  Chip,
  IconButton,
  Button,
  Paper,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Stop as StopIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  HourglassEmpty as PendingIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
} from '@material-ui/icons';
import { useApi } from '@backstage/core-plugin-api';
import { kubernetesApiRef } from '@backstage/plugin-kubernetes';
import { useEntity } from '@backstage/plugin-catalog-react';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  executionCard: {
    marginBottom: theme.spacing(2),
    position: 'relative',
  },
  runningIndicator: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
  },
  taskStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  taskProgress: {
    flex: 1,
    marginRight: theme.spacing(2),
  },
  logContainer: {
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4',
    fontFamily: 'monospace',
    fontSize: '0.875rem',
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    maxHeight: 400,
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  timelineContainer: {
    position: 'relative',
    paddingLeft: theme.spacing(4),
  },
  timelineItem: {
    position: 'relative',
    paddingBottom: theme.spacing(2),
    '&::before': {
      content: '""',
      position: 'absolute',
      left: -20,
      top: 8,
      bottom: -8,
      width: 2,
      backgroundColor: theme.palette.divider,
    },
    '&:last-child::before': {
      display: 'none',
    },
  },
  timelineDot: {
    position: 'absolute',
    left: -26,
    top: 4,
    width: 14,
    height: 14,
    borderRadius: '50%',
    backgroundColor: theme.palette.background.paper,
    border: `2px solid ${theme.palette.primary.main}`,
  },
  statsBox: {
    display: 'flex',
    justifyContent: 'space-around',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  statItem: {
    textAlign: 'center',
  },
  filterBar: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
  },
}));

// PipelineRun 详细状态
interface PipelineRunDetails {
  name: string;
  namespace: string;
  pipeline: string;
  status: 'Succeeded' | 'Running' | 'Failed' | 'Pending' | 'Cancelled';
  startTime?: string;
  completionTime?: string;
  duration?: string;
  tasks: TaskStatus[];
  params?: Record<string, string>;
  triggeredBy?: string;
}

// Task 状态
interface TaskStatus {
  name: string;
  status: 'Succeeded' | 'Running' | 'Failed' | 'Pending' | 'Skipped';
  startTime?: string;
  completionTime?: string;
  steps?: StepStatus[];
  retries?: number;
}

// Step 状态
interface StepStatus {
  name: string;
  status: 'Succeeded' | 'Running' | 'Failed' | 'Pending';
  container?: string;
  imageID?: string;
}

// 执行统计
interface ExecutionStats {
  total: number;
  succeeded: number;
  failed: number;
  running: number;
  pending: number;
  avgDuration: string;
  successRate: number;
}

// 任务状态图标
const TaskStatusIcon: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case 'Succeeded':
      return <SuccessIcon style={{ color: '#4caf50' }} />;
    case 'Running':
      return <CircularProgress size={20} />;
    case 'Failed':
      return <ErrorIcon style={{ color: '#f44336' }} />;
    case 'Pending':
      return <PendingIcon style={{ color: '#ff9800' }} />;
    case 'Cancelled':
    case 'Skipped':
      return <CancelIcon style={{ color: '#9e9e9e' }} />;
    default:
      return <PendingIcon />;
  }
};

// 执行详情对话框
const ExecutionDetailsDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  run: PipelineRunDetails | null;
}> = ({ open, onClose, run }) => {
  const classes = useStyles();
  const [selectedTask, setSelectedTask] = useState<TaskStatus | null>(null);
  const [logs, setLogs] = useState<string>('');
  const [loadingLogs, setLoadingLogs] = useState(false);
  const kubernetesApi = useApi(kubernetesApiRef);

  const fetchTaskLogs = async (task: TaskStatus) => {
    setLoadingLogs(true);
    try {
      // 获取 TaskRun Pod 的日志
      const response = await kubernetesApi.proxy({
        clusterName: 'local-cluster',
        path: `/api/v1/namespaces/${run?.namespace}/pods?labelSelector=tekton.dev/taskRun=${run?.name}-${task.name}`,
      });

      if (response.ok) {
        const data = await response.json();
        if (data.items && data.items.length > 0) {
          const pod = data.items[0];
          const logResponse = await kubernetesApi.proxy({
            clusterName: 'local-cluster',
            path: `/api/v1/namespaces/${run?.namespace}/pods/${pod.metadata.name}/log`,
          });
          
          if (logResponse.ok) {
            const logText = await logResponse.text();
            setLogs(logText || '暂无日志');
          }
        } else {
          setLogs('未找到相关 Pod');
        }
      }
    } catch (error) {
      setLogs(`获取日志失败: ${error}`);
    } finally {
      setLoadingLogs(false);
    }
  };

  if (!run) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Pipeline 执行详情: {run.name}</Typography>
          <Chip 
            label={run.status} 
            color={run.status === 'Succeeded' ? 'primary' : 'default'}
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* 基本信息 */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Pipeline
                    </Typography>
                    <Typography>{run.pipeline}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      命名空间
                    </Typography>
                    <Typography>{run.namespace}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      开始时间
                    </Typography>
                    <Typography>
                      {run.startTime ? new Date(run.startTime).toLocaleString() : '-'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      持续时间
                    </Typography>
                    <Typography>{run.duration || '-'}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* 任务列表 */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              任务列表
            </Typography>
            <List>
              {run.tasks.map((task, index) => (
                <React.Fragment key={task.name}>
                  <ListItem
                    button
                    selected={selectedTask?.name === task.name}
                    onClick={() => {
                      setSelectedTask(task);
                      fetchTaskLogs(task);
                    }}
                  >
                    <ListItemIcon>
                      <TaskStatusIcon status={task.status} />
                    </ListItemIcon>
                    <ListItemText
                      primary={task.name}
                      secondary={`状态: ${task.status}`}
                    />
                    {task.retries && task.retries > 0 && (
                      <Chip label={`重试 ${task.retries}`} size="small" />
                    )}
                  </ListItem>
                  {index < run.tasks.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Grid>

          {/* 日志查看器 */}
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              任务日志
            </Typography>
            {selectedTask ? (
              <Paper>
                <Box p={2}>
                  <Typography variant="subtitle2" gutterBottom>
                    {selectedTask.name}
                  </Typography>
                  {loadingLogs ? (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Box className={classes.logContainer}>
                      {logs || '请选择一个任务查看日志'}
                    </Box>
                  )}
                </Box>
              </Paper>
            ) : (
              <Alert severity="info">请选择一个任务查看日志</Alert>
            )}
          </Grid>

          {/* 参数信息 */}
          {run.params && Object.keys(run.params).length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                执行参数
              </Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>参数名</TableCell>
                      <TableCell>值</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(run.params).map(([key, value]) => (
                      <TableRow key={key}>
                        <TableCell>{key}</TableCell>
                        <TableCell>{value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>关闭</Button>
      </DialogActions>
    </Dialog>
  );
};

// 主监控组件
export const PipelineExecutionMonitor: React.FC = () => {
  const classes = useStyles();
  // 检查是否在实体上下文中
  let entity;
  try {
    const entityContext = useEntity();
    entity = entityContext.entity;
  } catch {
    // 不在实体上下文中，使用默认值
    entity = null;
  }
  const kubernetesApi = useApi(kubernetesApiRef);
  const [executions, setExecutions] = useState<PipelineRunDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedExecution, setSelectedExecution] = useState<PipelineRunDetails | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [filter, setFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [selectedNamespace, setSelectedNamespace] = useState<string>('tekton-pipelines');
  const [stats, setStats] = useState<ExecutionStats>({
    total: 0,
    succeeded: 0,
    failed: 0,
    running: 0,
    pending: 0,
    avgDuration: '0s',
    successRate: 0,
  });

  // 加载 PipelineRuns
  const loadExecutions = useCallback(async () => {
    try {
      setLoading(true);
      // 在 Control Plane 中使用选择的命名空间，在实体页面中使用实体的命名空间
      const namespace = entity?.metadata?.annotations?.['backstage.io/kubernetes-namespace'] || selectedNamespace;
      
      const response = await kubernetesApi.proxy({
        clusterName: 'local-cluster',
        path: `/apis/tekton.dev/v1beta1/namespaces/${namespace}/pipelineruns`,
      });

      if (response.ok) {
        const data = await response.json();
        const runs: PipelineRunDetails[] = data.items.map((run: any) => ({
          name: run.metadata.name,
          namespace: run.metadata.namespace,
          pipeline: run.spec?.pipelineRef?.name || 'unknown',
          status: getRunStatus(run),
          startTime: run.status?.startTime,
          completionTime: run.status?.completionTime,
          duration: calculateDuration(run.status?.startTime, run.status?.completionTime),
          tasks: extractTasks(run),
          params: extractParams(run),
          triggeredBy: run.metadata?.annotations?.['backstage.io/triggered-by'] || 'unknown',
        }));

        setExecutions(runs);
        calculateStats(runs);
      }
    } catch (error) {
      // Failed to load pipeline runs
    } finally {
      setLoading(false);
    }
  }, [entity, kubernetesApi, selectedNamespace]);

  // 获取运行状态
  const getRunStatus = (run: any): PipelineRunDetails['status'] => {
    const conditions = run.status?.conditions;
    if (!conditions || conditions.length === 0) return 'Pending';
    
    const succeeded = conditions.find((c: any) => c.type === 'Succeeded');
    if (!succeeded) return 'Pending';
    
    if (succeeded.status === 'True') return 'Succeeded';
    if (succeeded.status === 'False') {
      if (succeeded.reason === 'Running') return 'Running';
      if (succeeded.reason === 'Cancelled') return 'Cancelled';
      return 'Failed';
    }
    
    return 'Pending';
  };

  // 计算持续时间
  const calculateDuration = (startTime?: string, endTime?: string): string => {
    if (!startTime) return '-';
    
    const start = new Date(startTime).getTime();
    const end = endTime ? new Date(endTime).getTime() : Date.now();
    const duration = end - start;
    
    const minutes = Math.floor(duration / 60000);
    const seconds = Math.floor((duration % 60000) / 1000);
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  // 提取任务信息
  const extractTasks = (run: any): TaskStatus[] => {
    const taskRuns = run.status?.taskRuns || {};
    return Object.entries(taskRuns).map(([name, task]: [string, any]) => ({
      name: task.pipelineTaskName || name,
      status: getTaskStatus(task),
      startTime: task.status?.startTime,
      completionTime: task.status?.completionTime,
      steps: task.status?.steps || [],
      retries: task.status?.retriesStatus?.length || 0,
    }));
  };

  // 获取任务状态
  const getTaskStatus = (task: any): TaskStatus['status'] => {
    const conditions = task.status?.conditions;
    if (!conditions || conditions.length === 0) return 'Pending';
    
    const succeeded = conditions.find((c: any) => c.type === 'Succeeded');
    if (!succeeded) return 'Pending';
    
    if (succeeded.status === 'True') return 'Succeeded';
    if (succeeded.status === 'False') {
      if (succeeded.reason === 'Running') return 'Running';
      if (succeeded.reason === 'TaskRunCancelled') return 'Skipped';
      return 'Failed';
    }
    
    return 'Pending';
  };

  // 提取参数
  const extractParams = (run: any): Record<string, string> => {
    const params = run.spec?.params || [];
    return params.reduce((acc: Record<string, string>, param: any) => {
      acc[param.name] = param.value;
      return acc;
    }, {});
  };

  // 计算统计信息
  const calculateStats = (runs: PipelineRunDetails[]) => {
    const total = runs.length;
    const succeeded = runs.filter(r => r.status === 'Succeeded').length;
    const failed = runs.filter(r => r.status === 'Failed').length;
    const running = runs.filter(r => r.status === 'Running').length;
    const pending = runs.filter(r => r.status === 'Pending').length;
    
    const durations = runs
      .filter(r => r.duration && r.duration !== '-')
      .map(r => {
        const match = r.duration?.match(/(\d+)m?\s*(\d+)?s?/);
        if (match) {
          const minutes = parseInt(match[1]) || 0;
          const seconds = parseInt(match[2]) || 0;
          return minutes * 60 + seconds;
        }
        return 0;
      });
    
    const avgSeconds = durations.length > 0 
      ? durations.reduce((a, b) => a + b, 0) / durations.length 
      : 0;
    
    const avgDuration = avgSeconds > 60 
      ? `${Math.floor(avgSeconds / 60)}m ${Math.floor(avgSeconds % 60)}s`
      : `${Math.floor(avgSeconds)}s`;
    
    const successRate = total > 0 ? Math.round((succeeded / total) * 100) : 0;
    
    setStats({
      total,
      succeeded,
      failed,
      running,
      pending,
      avgDuration,
      successRate,
    });
  };

  // 停止执行
  const handleStop = async (run: PipelineRunDetails) => {
    if (!window.confirm(`确定要停止 ${run.name} 吗？`)) return;
    
    try {
      // 更新 PipelineRun 状态为取消
      const cancelPatch = {
        spec: {
          status: 'Cancelled',
        },
      };
      
      await kubernetesApi.proxy({
        clusterName: 'local-cluster',
        path: `/apis/tekton.dev/v1beta1/namespaces/${run.namespace}/pipelineruns/${run.name}`,
        init: {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/merge-patch+json',
          },
          body: JSON.stringify(cancelPatch),
        },
      });
      
      loadExecutions();
    } catch (error) {
      console.error('Failed to stop pipeline run:', error);
    }
  };

  // 自动刷新
  useEffect(() => {
    loadExecutions();
    
    if (autoRefresh) {
      const interval = setInterval(loadExecutions, 10000); // 每10秒刷新
      return () => clearInterval(interval);
    }
    return undefined;
  }, [loadExecutions, autoRefresh]);

  // 过滤执行
  const filteredExecutions = executions
    .filter(run => {
      if (filter !== 'all' && run.status !== filter) return false;
      if (searchTerm && !run.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      return true;
    })
    .sort((a, b) => {
      const aTime = new Date(a.startTime || 0).getTime();
      const bTime = new Date(b.startTime || 0).getTime();
      return bTime - aTime;
    });

  if (loading && executions.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* 标题栏 */}
      <Box className={classes.header} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">Pipeline 执行监控</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadExecutions}
            style={{ marginRight: 8 }}
          >
            刷新
          </Button>
          <Button
            variant={autoRefresh ? 'contained' : 'outlined'}
            onClick={() => setAutoRefresh(!autoRefresh)}
            color={autoRefresh ? 'primary' : 'default'}
          >
            自动刷新 {autoRefresh ? 'ON' : 'OFF'}
          </Button>
        </Box>
      </Box>

      {/* 统计信息 */}
      <Paper className={classes.statsBox}>
        <Box className={classes.statItem}>
          <Typography variant="h4">{stats.total}</Typography>
          <Typography variant="body2" color="textSecondary">总执行数</Typography>
        </Box>
        <Box className={classes.statItem}>
          <Typography variant="h4" style={{ color: '#4caf50' }}>{stats.succeeded}</Typography>
          <Typography variant="body2" color="textSecondary">成功</Typography>
        </Box>
        <Box className={classes.statItem}>
          <Typography variant="h4" style={{ color: '#f44336' }}>{stats.failed}</Typography>
          <Typography variant="body2" color="textSecondary">失败</Typography>
        </Box>
        <Box className={classes.statItem}>
          <Typography variant="h4" style={{ color: '#2196f3' }}>{stats.running}</Typography>
          <Typography variant="body2" color="textSecondary">运行中</Typography>
        </Box>
        <Box className={classes.statItem}>
          <Typography variant="h4">{stats.avgDuration}</Typography>
          <Typography variant="body2" color="textSecondary">平均时长</Typography>
        </Box>
        <Box className={classes.statItem}>
          <Typography variant="h4">{stats.successRate}%</Typography>
          <Typography variant="body2" color="textSecondary">成功率</Typography>
        </Box>
      </Paper>

      {/* 过滤器 */}
      <Paper className={classes.filterBar}>
        <TextField
          placeholder="搜索..."
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon />,
          }}
        />
        {!entity && (
          <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
            <InputLabel>命名空间</InputLabel>
            <Select
              value={selectedNamespace}
              onChange={(e) => setSelectedNamespace(e.target.value as string)}
              label="命名空间"
            >
              <MenuItem value="tekton-pipelines">tekton-pipelines</MenuItem>
              <MenuItem value="default">default</MenuItem>
              <MenuItem value="payment">payment</MenuItem>
              <MenuItem value="order">order</MenuItem>
              <MenuItem value="idp">idp</MenuItem>
            </Select>
          </FormControl>
        )}
        <Button
          variant={filter === 'all' ? 'contained' : 'outlined'}
          onClick={() => setFilter('all')}
          size="small"
        >
          全部
        </Button>
        <Button
          variant={filter === 'Running' ? 'contained' : 'outlined'}
          onClick={() => setFilter('Running')}
          size="small"
          color="primary"
        >
          运行中
        </Button>
        <Button
          variant={filter === 'Succeeded' ? 'contained' : 'outlined'}
          onClick={() => setFilter('Succeeded')}
          size="small"
          style={{ color: filter === 'Succeeded' ? '#fff' : '#4caf50', backgroundColor: filter === 'Succeeded' ? '#4caf50' : undefined }}
        >
          成功
        </Button>
        <Button
          variant={filter === 'Failed' ? 'contained' : 'outlined'}
          onClick={() => setFilter('Failed')}
          size="small"
          style={{ color: filter === 'Failed' ? '#fff' : '#f44336', backgroundColor: filter === 'Failed' ? '#f44336' : undefined }}
        >
          失败
        </Button>
      </Paper>

      {/* 执行列表 */}
      {filteredExecutions.map(run => (
        <Card key={run.name} className={classes.executionCard}>
          {run.status === 'Running' && (
            <LinearProgress className={classes.runningIndicator} />
          )}
          <CardHeader
            title={
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                <TaskStatusIcon status={run.status} />
                <Typography variant="h6">{run.name}</Typography>
              </Box>
            }
            subheader={
              <Box display="flex" style={{ gap: 16 }}>
                <Typography variant="body2">Pipeline: {run.pipeline}</Typography>
                <Typography variant="body2">命名空间: {run.namespace}</Typography>
                <Typography variant="body2">触发: {run.triggeredBy}</Typography>
              </Box>
            }
            action={
              <Box>
                {run.status === 'Running' && (
                  <IconButton onClick={() => handleStop(run)}>
                    <StopIcon />
                  </IconButton>
                )}
                <IconButton 
                  onClick={() => {
                    setSelectedExecution(run);
                    setDetailsOpen(true);
                  }}
                >
                  <VisibilityIcon />
                </IconButton>
              </Box>
            }
          />
          <CardContent>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <Typography variant="subtitle2" gutterBottom>
                  任务进度
                </Typography>
                <Box>
                  {run.tasks.map(task => (
                    <Box key={task.name} className={classes.taskStatus}>
                      <TaskStatusIcon status={task.status} />
                      <Typography variant="body2">{task.name}</Typography>
                      <Box className={classes.taskProgress}>
                        <LinearProgress
                          variant="determinate"
                          value={
                            task.status === 'Succeeded' ? 100 :
                            task.status === 'Running' ? 50 :
                            task.status === 'Failed' ? 100 : 0
                          }
                          color={
                            task.status === 'Succeeded' ? 'primary' :
                            task.status === 'Failed' ? 'secondary' : 'primary'
                          }
                        />
                      </Box>
                      <Typography variant="caption">{task.status}</Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" gutterBottom>
                  时间信息
                </Typography>
                <Box>
                  <Typography variant="body2">
                    开始: {run.startTime ? new Date(run.startTime).toLocaleString() : '-'}
                  </Typography>
                  <Typography variant="body2">
                    持续: {run.duration}
                  </Typography>
                  {run.completionTime && (
                    <Typography variant="body2">
                      完成: {new Date(run.completionTime).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ))}

      {/* 执行详情对话框 */}
      <ExecutionDetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        run={selectedExecution}
      />
    </Box>
  );
};

export default PipelineExecutionMonitor;

